/**
 * Force-modal shown on the portal dashboard to any signed-in client who
 * doesn't have an MPIN set yet (typically anyone whose account pre-dates
 * the MPIN feature).  Cannot be dismissed until an MPIN is chosen — the
 * whole point is to give them a way to sign back in on their next visit.
 */
import { useEffect, useState } from "react";
import portalApi from "@/lib/portalApi";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";

const cleanMpin = (v) => (v || "").replace(/\D/g, "").slice(0, 4);
const WEAK = new Set(["0000","1111","2222","3333","4444","5555","6666","7777","8888","9999","1234","4321"]);

export default function SetMpinModal({ onDone }) {
    const [checking, setChecking] = useState(true);
    const [hasMpin, setHasMpin] = useState(true);   // assume yes → hide by default
    const [mpin, setMpin] = useState("");
    const [mpin2, setMpin2] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        portalApi.get("/mpin-status")
            .then(r => setHasMpin(!!r.data.has_mpin))
            .catch(() => setHasMpin(true))          // fail-open, don't block
            .finally(() => setChecking(false));
    }, []);

    const valid = mpin.length === 4 && !WEAK.has(mpin);
    const matches = valid && mpin === mpin2;
    const hint = !valid
        ? (mpin.length < 4 ? "Enter a 4-digit MPIN"
           : "That MPIN is too easy to guess — try another")
        : (!matches ? "Both entries must match" : "");

    const submit = async () => {
        if (!matches) { toast.error(hint || "Enter a valid MPIN"); return; }
        setSubmitting(true);
        try {
            await portalApi.post("/set-mpin", { mpin });
            toast.success("MPIN saved. Use it next time to sign in from any device.");
            setHasMpin(true);
            onDone?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save MPIN");
        } finally { setSubmitting(false); }
    };

    if (checking || hasMpin) return null;

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
             data-testid="portal-set-mpin-modal">
            <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 space-y-4">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-emerald-100 grid place-items-center">
                        <KeyRound size={20} className="text-emerald-700"/>
                    </div>
                    <div>
                        <h2 className="text-lg font-black text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>
                            Set your MPIN
                        </h2>
                        <p className="text-xs text-slate-500">
                            One-time setup so you can sign back in from any device.
                        </p>
                    </div>
                </div>

                <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 block">
                    New 4-digit MPIN
                </label>
                <div className="flex items-center gap-2 px-3 rounded-xl border-2 border-slate-200 focus-within:border-emerald-500 bg-white">
                    <KeyRound size={16} className="text-slate-400 shrink-0"/>
                    <input type="password" inputMode="numeric" maxLength={4}
                           value={mpin}
                           onChange={(e) => setMpin(cleanMpin(e.target.value))}
                           placeholder="• • • •"
                           autoFocus
                           className="flex-1 py-3 outline-none text-base tracking-widest bg-transparent"
                           data-testid="portal-set-mpin-input"/>
                </div>

                <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 block">
                    Confirm MPIN
                </label>
                <div className="flex items-center gap-2 px-3 rounded-xl border-2 border-slate-200 focus-within:border-emerald-500 bg-white">
                    <KeyRound size={16} className="text-slate-400 shrink-0"/>
                    <input type="password" inputMode="numeric" maxLength={4}
                           value={mpin2}
                           onChange={(e) => setMpin2(cleanMpin(e.target.value))}
                           onPaste={(e) => { e.preventDefault(); toast.error("Please type the MPIN again."); }}
                           placeholder="Re-enter the same 4 digits"
                           className="flex-1 py-3 outline-none text-base tracking-widest bg-transparent"
                           data-testid="portal-set-mpin-input2"/>
                </div>

                <div className="text-[11px] text-slate-500 min-h-[16px]">{hint}</div>

                <button onClick={submit} disabled={!matches || submitting}
                        className={`w-full py-3 rounded-xl font-black text-white text-sm shadow-lg transition-all
                                    ${matches && !submitting
                                      ? "bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-500 active:scale-[0.98]"
                                      : "bg-slate-300 cursor-not-allowed"}`}
                        data-testid="portal-set-mpin-submit">
                    {submitting
                        ? <><Loader2 size={14} className="inline animate-spin mr-1"/> Saving…</>
                        : <><ShieldCheck size={14} className="inline mr-1"/> Save MPIN</>}
                </button>

                <p className="text-[11px] text-slate-400 text-center">
                    We store your MPIN securely. HDB staff can never see it.
                </p>
            </div>
        </div>
    );
}
